import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Card } from '../../components/Card';
import { EmptyState } from '../../components/EmptyState';
import { Field } from '../../components/Field';
import { KpiCard } from '../../components/KpiCard';
import { PageHeader } from '../../components/PageHeader';
import { Select } from '../../components/Select';
import { dashboardApi } from '../../lib/apiClient';
import { fmtInt } from '../../lib/format';
import { useUi } from '../../stores/ui';

function fmtDateTime(value?: string): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('th-TH', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function SyncHealthView() {
  const branchId = useUi((state) => state.branchId);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const deviceHealth = useQuery({
    queryKey: ['device-health', branchId],
    queryFn: () =>
      dashboardApi.getDeviceHealth({
        branchId: branchId ?? undefined,
      }),
  });

  useEffect(() => {
    const firstDeviceId = deviceHealth.data?.devices[0]?.deviceId ?? null;
    setSelectedDeviceId((current) =>
      current &&
      deviceHealth.data?.devices.some((device) => device.deviceId === current)
        ? current
        : firstDeviceId,
    );
  }, [deviceHealth.data]);

  const syncHealth = useQuery({
    queryKey: ['sync-health', selectedDeviceId, branchId],
    queryFn: () =>
      dashboardApi.getSyncHealthReport({
        deviceId: selectedDeviceId!,
        branchId: branchId ?? undefined,
      }),
    enabled: !!selectedDeviceId,
  });

  return (
    <div className="bo-fade">
      <PageHeader
        title="สถานะ Sync"
        subtitle="held gaps · dead letters · pending/applied/failed ต่อเครื่อง"
      />
      <div className="bo-card mb-6 flex flex-col gap-3 p-5 sm:p-6 md:flex-row md:items-end">
        <Field label="เลือกเครื่อง" className="w-full md:max-w-sm">
          <Select
            aria-label="เลือกเครื่อง"
            value={selectedDeviceId ?? ''}
            onChange={(event) =>
              setSelectedDeviceId(event.target.value || null)
            }
          >
            {(deviceHealth.data?.devices ?? []).map((device) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.deviceId} · {device.branchId}
              </option>
            ))}
          </Select>
        </Field>
        {selectedDeviceId && (
          <div className="text-sm-label text-ink-3 md:pb-2.5">
            แสดงรายละเอียด held gap และ DLQ ของ `{selectedDeviceId}`
          </div>
        )}
      </div>

      {!selectedDeviceId ? (
        <div className="bo-card">
          <EmptyState
            title="ยังไม่มีเครื่องใน scope"
            sub="ต้องมี device ที่ขึ้นใน device-health ก่อนถึงจะเปิดดู sync detail ได้"
          />
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-4 mb-6">
            <KpiCard
              label="Pending"
              value={
                syncHealth.isLoading
                  ? '…'
                  : fmtInt(syncHealth.data?.pendingCount ?? 0)
              }
              tone="warn"
            />
            <KpiCard
              label="Failed"
              value={
                syncHealth.isLoading
                  ? '…'
                  : fmtInt(syncHealth.data?.failedCount ?? 0)
              }
              tone="danger"
            />
            <KpiCard
              label="Blocked"
              value={
                syncHealth.isLoading
                  ? '…'
                  : fmtInt(syncHealth.data?.blockedCount ?? 0)
              }
              tone="warn"
            />
            <KpiCard
              label="Last Applied"
              value={
                syncHealth.isLoading
                  ? '…'
                  : fmtInt(syncHealth.data?.lastAppliedSequence ?? 0)
              }
              sub={fmtDateTime(syncHealth.data?.lastAppliedAt)}
            />
          </div>
          <div className="grid gap-6 xl:grid-cols-2">
            <Card title="Held Gaps">
              <div className="overflow-x-auto">
                <table className="bo-table">
                  <thead>
                    <tr>
                      <th>Seq</th>
                      <th>Event</th>
                      <th>Entity</th>
                      <th>Held At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(syncHealth.data?.gaps ?? []).map((gap) => (
                      <tr key={gap.eventId}>
                        <td className="num">{fmtInt(gap.eventSequence)}</td>
                        <td>{gap.entityType}</td>
                        <td>{gap.entityId}</td>
                        <td>{fmtDateTime(gap.createdAt)}</td>
                      </tr>
                    ))}
                    {(syncHealth.data?.gaps ?? []).length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center text-ink-3 py-4">
                          ไม่มี held gap
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
            <Card title="Dead Letters">
              <div className="overflow-x-auto">
                <table className="bo-table">
                  <thead>
                    <tr>
                      <th>Seq</th>
                      <th>Event</th>
                      <th>Error</th>
                      <th>Status</th>
                      <th>Attempts</th>
                      <th>ล่าสุด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(syncHealth.data?.dlq ?? []).map((entry) => (
                      <tr key={entry.eventId}>
                        <td className="num">{fmtInt(entry.eventSequence)}</td>
                        <td>{entry.entityType}</td>
                        <td>{entry.lastError ?? '—'}</td>
                        <td>{entry.resolutionStatus ?? '—'}</td>
                        <td className="num">{fmtInt(entry.retryCount)}</td>
                        <td>{fmtDateTime(entry.lastAttemptedAt)}</td>
                      </tr>
                    ))}
                    {(syncHealth.data?.dlq ?? []).length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-ink-3 py-4">
                          ไม่มี DLQ เปิดค้าง
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
